import { apiFetch } from "./apiFetch";

function mapConnectionToLegacyShape(connection) {
  const activeSprint = connection?.active_sprint || null;
  const latestSprint = connection?.latest_sprint || null;
  const status = activeSprint?.status || connection?.status || "active";
  const legacyConnectionId = activeSprint?.legacy_connection_id || null;

  return {
    id: activeSprint?.id || connection?.id,
    status: "accepted",
    other_user: connection?.other_user || null,
    connection: {
      id: legacyConnectionId,
      network_connection_id: connection?.id || null,
      sprint_id: activeSprint?.id || null,
      status,
      start_date: activeSprint?.start_date || null,
      end_date: activeSprint?.end_date || null,
      duration_days: activeSprint?.duration_days || null,
      recovery: activeSprint?.recovery || null,
    },
    active_sprint: activeSprint,
    latest_sprint: latestSprint,
  };
}

export async function fetchTrustBudConnections() {
  const result = await apiFetch("/connections/");
  return Array.isArray(result) ? result : [];
}

export async function fetchTrustBudDashboardList() {
  const connections = await fetchTrustBudConnections();
  return connections.map(mapConnectionToLegacyShape);
}

export async function fetchActiveSprint() {
  try {
    return await apiFetch("/sprints/active/");
  } catch (error) {
    if (error?.status === 404 || error?.message?.includes("404")) {
      return null;
    }
    throw error;
  }
}

export async function fetchSprintRecovery(sprintId) {
  if (!sprintId) return null;
  return apiFetch(`/sprints/${sprintId}/recovery/`);
}

export async function submitSprintRecoveryAction(sprintId, actionType) {
  if (!sprintId || !actionType) {
    throw new Error("Challenge recovery request requires sprintId and actionType.");
  }
  return apiFetch(`/sprints/${sprintId}/recovery/`, {
    method: "POST",
    body: JSON.stringify({ action_type: actionType }),
  });
}

export async function fetchSprintJourney(sprintId) {
  if (!sprintId) return null;
  return apiFetch(`/sprints/${sprintId}/journey/`);
}

export async function fetchSprintMessages(sprintId, limit = 10) {
  if (!sprintId) return null;
  return apiFetch(`/sprints/${sprintId}/messages/?limit=${limit}`);
}

export async function markSprintJourneyRead(sprintId) {
  if (!sprintId) return null;
  return apiFetch(`/sprints/${sprintId}/journey/read/`, { method: "POST" });
}

export async function sendSprintMessage(sprintId, body) {
  if (!sprintId) {
    throw new Error("Challenge message requires sprintId.");
  }
  return apiFetch(`/sprints/${sprintId}/messages/`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
}

export async function fetchSprintReflectionToday({ sprintId, userId } = {}) {
  if (sprintId) {
    return apiFetch(`/sprints/${sprintId}/reflection/today/`);
  }
  if (!userId) return null;
  return apiFetch(`/companions/${userId}/reflection/today/`);
}

export async function fetchSprintReflectionWeek({ sprintId, userId } = {}) {
  if (sprintId) {
    return apiFetch(`/sprints/${sprintId}/reflection/week/`);
  }
  if (!userId) return null;
  return apiFetch(`/companions/${userId}/reflection/week/`);
}

export async function submitSprintReflection({ sprintId, userId, payload }) {
  if (sprintId) {
    return apiFetch(`/sprints/${sprintId}/reflection/today/`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }
  if (!userId) {
    throw new Error("Challenge reflection requires sprintId or userId.");
  }
  return apiFetch(`/companions/${userId}/reflection/today/`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function submitChallengeShowUp(sprintId, status) {
  if (!sprintId) {
    throw new Error("Challenge status requires sprintId.");
  }
  return apiFetch(`/accountability/${sprintId}/daily-report/`, {
    method: "POST",
    body: JSON.stringify({ status }),
  });
}

export async function fetchAccountabilityDailyReport(sprintId) {
  if (!sprintId) return null;
  return apiFetch(`/accountability/${sprintId}/daily-report/`);
}

export async function submitAccountabilityTimerReport(sprintId, durationSeconds = 300) {
  if (!sprintId) {
    throw new Error("Accountability timer report requires sprintId.");
  }
  return apiFetch(`/accountability/${sprintId}/timer-report/`, {
    method: "POST",
    body: JSON.stringify({ duration_seconds: durationSeconds }),
  });
}

export async function submitAccountabilityPhotoProof(sprintId, file) {
  if (!sprintId || !file) {
    throw new Error("Accountability photo proof requires sprintId and file.");
  }
  const form = new FormData();
  form.append("status", "done");
  form.append("photo", file);
  return apiFetch(`/accountability/${sprintId}/photo-proof/`, {
    method: "POST",
    body: form,
  });
}

export async function acknowledgeAccountabilityReport(sprintId, reportId) {
  if (!sprintId || !reportId) {
    throw new Error("Accountability acknowledgment requires sprintId and reportId.");
  }
  return apiFetch(`/accountability/${sprintId}/daily-report/${reportId}/ack/`, {
    method: "POST",
  });
}

export async function sendSprintAcknowledgment(sprintId) {
  if (!sprintId) {
    throw new Error("Challenge acknowledgment requires sprintId.");
  }
  return apiFetch(`/sprints/${sprintId}/acknowledgments/`, { method: "POST" });
}

export async function requestSprintEncouragement(sprintId) {
  if (!sprintId) {
    throw new Error("Challenge encouragement requires sprintId.");
  }
  return apiFetch(`/sprints/${sprintId}/encouragement/`, { method: "POST" });
}

export async function renewConnection(connectionId, rating = "", sprintMode = null) {
  if (!connectionId) {
    throw new Error("Connection renewal requires connectionId.");
  }
  const payload = {
    ...(rating ? { rating } : {}),
    ...(sprintMode ? { sprint_mode: sprintMode } : {}),
  };
  return apiFetch(`/connections/${connectionId}/renew/`, {
    method: "POST",
    body: Object.keys(payload).length ? JSON.stringify(payload) : null,
  });
}

export async function closeConnection(connectionId, rating = "") {
  if (!connectionId) {
    throw new Error("Connection close requires connectionId.");
  }
  return apiFetch(`/connections/${connectionId}/close/`, {
    method: "POST",
    body: rating ? JSON.stringify({ rating }) : null,
  });
}

export async function disconnectConnection(connectionId, rating = "") {
  if (!connectionId) {
    throw new Error("Connection disconnect requires connectionId.");
  }
  return apiFetch(`/connections/${connectionId}/disconnect/`, {
    method: "POST",
    body: rating ? JSON.stringify({ rating }) : null,
  });
}

export async function acknowledgeTrustBud({ sprintId, userId }) {
  if (sprintId) return sendSprintAcknowledgment(sprintId);
  if (!userId) throw new Error("TrustBud acknowledgment requires a Challenge or TrustBud user.");
  return apiFetch(`/companions/${userId}/acknowledgments/`, { method: "POST" });
}

export async function renewTrustBudRelationship({ connectionId, userId, rating = "" }) {
  if (connectionId) return renewConnection(connectionId, rating);
  if (!userId) throw new Error("TrustBud renewal requires connectionId or userId.");
  return apiFetch(`/companions/${userId}/renew/`, {
    method: "POST",
    body: rating ? JSON.stringify({ rating }) : null,
  });
}

export async function closeTrustBudRelationship({ connectionId, userId, rating = "" }) {
  if (connectionId) return closeConnection(connectionId, rating);
  if (!userId) throw new Error("TrustBud close requires connectionId or userId.");
  return apiFetch(`/companions/${userId}/close/`, {
    method: "POST",
    body: rating ? JSON.stringify({ rating }) : null,
  });
}

export async function disconnectTrustBudRelationship({ connectionId, userId, rating = "" }) {
  if (connectionId) return disconnectConnection(connectionId, rating);
  if (!userId) throw new Error("TrustBud disconnect requires connectionId or userId.");
  return apiFetch(`/companions/${userId}/disconnect/`, {
    method: "POST",
    body: rating ? JSON.stringify({ rating }) : null,
  });
}

export async function fetchConnectionContext(connectionId) {
  if (!connectionId) {
    throw new Error("Connection context requires connectionId.");
  }
  return apiFetch(`/connections/${connectionId}/context/`);
}

export async function fetchSprintPartnerProfile(sprintId) {
  if (!sprintId) {
    throw new Error("Challenge partner profile requires sprintId.");
  }
  return apiFetch(`/sprints/${sprintId}/partner-profile/`);
}

export async function fetchNetworkChats() {
  return apiFetch("/activity/chats/");
}

export async function fetchNetworkChat(connectionId, limit = 50) {
  if (!connectionId) {
    throw new Error("Message thread requires connectionId.");
  }
  return apiFetch(`/activity/chats/${connectionId}/?limit=${limit}`);
}

export async function sendNetworkChatMessage(connectionId, body, sprintId = null) {
  if (!connectionId) {
    throw new Error("Message requires connectionId.");
  }
  return apiFetch(`/activity/chats/${connectionId}/messages/`, {
    method: "POST",
    body: JSON.stringify({
      body,
      ...(sprintId ? { sprintId } : {}),
    }),
  });
}

export async function markNetworkChatRead(connectionId) {
  if (!connectionId) {
    throw new Error("Message read requires connectionId.");
  }
  return apiFetch(`/activity/chats/${connectionId}/read/`, {
    method: "POST",
  });
}

export async function startSprintForConnection(connectionId, durationDays = 7, sprintMode = "support", challengeFocus = null) {
  if (!connectionId) {
    throw new Error("Challenge start requires connectionId.");
  }
  return apiFetch("/sprints/", {
    method: "POST",
    body: JSON.stringify({
      connection_id: connectionId,
      duration_days: durationDays,
      sprint_mode: sprintMode || "support",
      ...(challengeFocus ? { challenge_focus: challengeFocus } : {}),
    }),
  });
}

export async function startSprint(connectionId, options = {}) {
  const result = await startSprintForConnection(
    connectionId,
    options.durationDays || 7,
    options.sprintMode || options.sprint_mode || "support",
    options.challengeFocus || options.challenge_focus || null
  );
  const sprintId = result?.sprint_id || result?.active_sprint_id || null;
  return {
    ...result,
    route: sprintId
      ? getSprintWorkspacePath({
          sprintId,
          connectionId,
          userId: options.userId,
          focus: options.focus || "chat",
          source: options.source,
        })
      : null,
  };
}

export function getSprintWorkspacePath({ sprintId, connectionId, userId, focus, source } = {}) {
  const params = new URLSearchParams();
  if (sprintId) params.set("sprintId", String(sprintId));
  if (connectionId) params.set("connectionId", String(connectionId));
  if (userId) params.set("userId", String(userId));
  if (focus) params.set("focus", String(focus));
  if (source) params.set("source", String(source));
  const query = params.toString();
  return query ? `/sprint?${query}` : "/sprint";
}

export function getNetworkPath({ tab = "messages", connectionId, userId, compose, message, quickAction } = {}) {
  const params = new URLSearchParams();
  params.set("tab", tab === "chats" ? "messages" : tab);
  if (connectionId) params.set("connectionId", String(connectionId));
  if (userId) params.set("userId", String(userId));
  if (compose) params.set("compose", "1");
  if (message) params.set("message", String(message));
  if (quickAction) params.set("quickAction", String(quickAction));
  return `/activity?${params.toString()}`;
}

export function getTrustBudDashboardPath({ sprintId, connectionId, userId } = {}) {
  return getSprintWorkspacePath({ sprintId, connectionId, userId });
}
